/**
 * PromoterFeeReconciliation — Conversion Engine reporting Report B (Stage 4).
 *
 * Per promoter event: the INPUTS a promoter fee would be reconciled from —
 * attendance, vouchers issued/activated, new-member vouchers, friends brought
 * vs activated, and current value. No fee column yet (the fee formula is a
 * pending business decision; this report is inputs-only by design).
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getPromoterFeeReconciliation,
  type PromoterReconciliationRow,
} from "../../services/conversionReports.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";

const PAGE_SIZE = 50;
const n = (v: unknown): number => Number(v ?? 0);
const money = (v: unknown): string => `$${n(v).toFixed(2)}`;
const formatDate = (d?: string | null): string => {
  if (!d) {
    return "";
  }
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString();
  } catch {
    return d;
  }
};

const filterToRpc = (f: ReportFilterState, page: number, limit: number) => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  page,
  limit,
});

const EXPORT_COLUMNS = [
  "Promoter",
  "Event",
  "Event Date",
  "Type",
  "State",
  "Attendance",
  "Vouchers Issued",
  "Activated",
  "New-Member Vouchers",
  "Friends Brought",
  "Friends Activated",
  "Current Value",
];

const PromoterFeeReconciliation: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [filters.dateFrom, filters.dateTo, filters.orgId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-promoter-reconciliation", filters, page],
    queryFn: () =>
      getPromoterFeeReconciliation(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const mapRows = (
    pageRows: PromoterReconciliationRow[],
  ): Record<string, unknown>[] =>
    pageRows.map((r) => ({
      Promoter: r.promoter_name,
      Event: r.event_title,
      "Event Date": formatDate(r.event_start_date),
      Type: r.event_type,
      State: r.event_location_state || "",
      Attendance: n(r.attendance_count),
      "Vouchers Issued": n(r.vouchers_issued),
      Activated: n(r.vouchers_activated),
      "New-Member Vouchers": n(r.new_member_vouchers),
      "Friends Brought": n(r.friends_brought),
      "Friends Activated": n(r.friends_activated),
      "Current Value": n(r.current_value_total).toFixed(2),
    }));

  return (
    <div className="space-y-4">
      <ReportPageHeader
        title="Promoter Attendance & Fee Reconciliation"
        description={
          <>
            Per promoter event — attendance, vouchers issued/activated, friends
            brought vs activated, and value. Inputs only; the fee formula is a
            pending business decision and is not yet applied.
          </>
        }
        actions={
          <ExportButton
            filters={filters}
            fetchPage={(f, p, l) =>
              getPromoterFeeReconciliation(filterToRpc(f, p, l)).then(
                (res) => ({
                  rows: res.rows,
                  total: res.total,
                }),
              )
            }
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="promoter-fee-reconciliation"
          />
        }
      />

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showState={false}
        showType={false}
        showStatus={false}
        showOrg={true}
      />

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Promoter</th>
              <th className="px-3 py-2">Event</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2 text-right">Attendance</th>
              <th className="px-3 py-2 text-right">Issued</th>
              <th className="px-3 py-2 text-right">Activated</th>
              <th className="px-3 py-2 text-right">New Members</th>
              <th className="px-3 py-2 text-right">Friends (brought/act)</th>
              <th className="px-3 py-2 text-right">Current $</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-gray-500">
                  No promoter events with conversion activity yet.
                </td>
              </tr>
            )}
            {rows.map((r: PromoterReconciliationRow) => (
              <tr key={r.event_id} className="hover:bg-gray-50">
                <td className="px-3 py-2">{r.promoter_name}</td>
                <td className="px-3 py-2">
                  <Link
                    to={`/events/${r.event_id}`}
                    className="text-blue-600 hover:underline"
                  >
                    {r.event_title}
                  </Link>
                </td>
                <td className="px-3 py-2">{formatDate(r.event_start_date)}</td>
                <td className="px-3 py-2 text-right">
                  {n(r.attendance_count)}
                </td>
                <td className="px-3 py-2 text-right">{n(r.vouchers_issued)}</td>
                <td className="px-3 py-2 text-right">
                  {n(r.vouchers_activated)}
                </td>
                <td className="px-3 py-2 text-right">
                  {n(r.new_member_vouchers)}
                </td>
                <td className="px-3 py-2 text-right">
                  {n(r.friends_brought)} / {n(r.friends_activated)}
                </td>
                <td className="px-3 py-2 text-right">
                  {money(r.current_value_total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} total`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoterFeeReconciliation;
